import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Heart,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  ExternalLink,
  Download,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const TEAM_REGISTRATION_URL = "https://www.namiwalks.org/teams/87329";
const CAMPAIGN_FLYER_IMAGE =
  "https://vibe.filesafe.space/1787327131284421342/attachments/5d070daa-2b65-4d10-8628-8f9310168420.png";

const NamiWalksPage = () => {
  const scrollToDetails = () => {
    const el = document.getElementById("event-details");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SEOHead
        title="NAMIWalks Broward 2026 | Join Neuro Wellness"
        description="Join Neuro Wellness TMS Centers of America at NAMIWalks Broward County on October 10, 2026. Patients, families, friends and community supporters are invited to walk with our team."
        canonical="/namiwalks"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "Event",
            name: "NAMIWalks Broward 2026 — Walk With Neuro Wellness",
            description:
              "Join Neuro Wellness TMS Centers of America at NAMIWalks Broward County on October 10, 2026, walking together for mental health awareness, hope, and community.",
            startDate: "2026-10-10T08:00:00-04:00",
            endDate: "2026-10-10T12:00:00-04:00",
            eventAttendanceMode:
              "https://schema.org/OfflineEventAttendanceMode",
            eventStatus: "https://schema.org/EventScheduled",
            location: {
              "@type": "Place",
              name: "Nova Southeastern University",
              address: {
                "@type": "PostalAddress",
                streetAddress: "3100 Ray Ferrero Jr. Blvd.",
                addressLocality: "Davie",
                addressRegion: "FL",
                postalCode: "33314",
                addressCountry: "US",
              },
            },
            image: [CAMPAIGN_FLYER_IMAGE],
            performer: {
              "@type": "Organization",
              name: "Neuro Wellness TMS Centers of America",
            },
            organizer: {
              "@type": "MedicalClinic",
              name: "Neuro Wellness TMS Centers of America",
              url: "https://neurowellnesstms.com/",
            },
            offers: {
              "@type": "Offer",
              url: TEAM_REGISTRATION_URL,
              price: "0",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              validFrom: "2026-09-01T00:00:00-04:00",
            },
          },
        ]}
      />

      <Navbar />

      <main className="flex-1 pb-20 md:pb-0">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background pt-8 pb-14 md:pt-14 md:pb-20">
          <div className="container-px mx-auto max-w-7xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              {/* Left Column: Copy & Actions */}
              <div className="space-y-6 lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3.5 py-1 text-xs sm:text-sm font-semibold uppercase tracking-wider text-accent">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Community Event • NAMIWalks Broward 2026</span>
                </div>

                <h1 className="font-headings text-3xl font-extrabold tracking-tight text-primary sm:text-4xl md:text-5xl lg:text-5xl leading-[1.12]">
                  Walk With <span className="text-accent">Neuro Wellness</span>{" "}
                  at NAMIWalks Broward 2026
                </h1>

                <p className="text-base sm:text-lg leading-relaxed text-foreground/90 max-w-2xl">
                  Join Neuro Wellness TMS Centers of America, along with
                  patients, families, friends, caregivers, team members, and
                  community supporters, as we walk together for mental health
                  awareness, hope, and stronger communities.
                </p>

                {/* Primary & Secondary CTAs */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center pt-2">
                  <Button
                    asChild
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-md text-sm sm:text-base h-12 px-4 sm:px-6 justify-center"
                  >
                    <a
                      href={TEAM_REGISTRATION_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-center"
                    >
                      <span className="leading-tight">
                        JOIN THE NEURO WELLNESS TEAM
                      </span>
                      <ExternalLink className="h-4 w-4 shrink-0" />
                    </a>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={scrollToDetails}
                    className="border-primary text-primary hover:bg-primary hover:text-white rounded-xl font-semibold h-12 px-4 sm:px-6 justify-center"
                  >
                    View Event Details
                  </Button>
                </div>

                {/* Quick Trust / Info Badges */}
                <div className="pt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 text-xs sm:text-sm text-foreground/80">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <Calendar className="h-4 w-4 text-accent shrink-0" />
                    Saturday, Oct 10, 2026
                  </span>
                  <span className="hidden sm:inline text-muted-foreground/50">
                    •
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <MapPin className="h-4 w-4 text-accent shrink-0" />
                    Nova Southeastern University (Davie, FL)
                  </span>
                  <span className="hidden sm:inline text-muted-foreground/50">
                    •
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-accent">
                    Free Participation
                  </span>
                </div>
              </div>

              {/* Right Column: Campaign Flyer Graphic */}
              <div className="lg:col-span-5 flex flex-col items-center gap-4">
                <a
                  href={CAMPAIGN_FLYER_IMAGE}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="group relative block w-full max-w-[18rem] sm:max-w-md lg:max-w-lg rounded-2xl p-2 bg-gradient-to-br from-primary/10 via-accent/15 to-primary/5 shadow-premium border border-border transition-shadow hover:shadow-glow"
                  aria-label="Open full-size NAMIWalks campaign flyer (opens in new tab)"
                >
                  <div className="overflow-hidden rounded-xl bg-card">
                    <img
                      src={CAMPAIGN_FLYER_IMAGE}
                      alt="Neuro Wellness TMS Centers of America NAMIWalks Broward 2026 campaign flyer — walk with our team for mental health awareness"
                      className="w-full h-auto object-cover"
                      width={1080}
                      height={1080}
                      loading="eager"
                    />
                  </div>
                  <span className="absolute -top-2 -right-2 rounded-full bg-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-md">
                    Tap to enlarge
                  </span>
                </a>

                <a
                  href={CAMPAIGN_FLYER_IMAGE}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline underline-offset-2"
                >
                  <Download className="h-4 w-4" />
                  Download Flyer
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* EVENT DETAILS SECTION */}
        <section
          id="event-details"
          className="scroll-mt-24 py-12 md:py-16 bg-muted/50 border-y border-border"
        >
          <div className="container-px mx-auto max-w-6xl">
            <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-accent">
                NAMIWalks Broward County
              </span>
              <h2 className="font-headings text-2xl sm:text-3xl md:text-4xl font-extrabold text-primary">
                Join Us for the Walk
              </h2>
              <p className="text-foreground/80 text-sm sm:text-base">
                Here is everything you need to know about joining the Neuro
                Wellness walk team.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {/* Card 1: Date & Time */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-3">
                <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  <Calendar className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Date &amp; Time
                </h3>
                <div className="space-y-1 text-sm sm:text-base text-foreground/90">
                  <p className="font-bold text-primary">
                    Saturday, October 10, 2026
                  </p>
                  <p className="text-foreground/80">8:00 AM – 12:00 PM</p>
                  <div className="pt-2 flex items-center gap-1.5 text-xs text-accent font-semibold">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Check-in begins at 8:00 AM</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Location */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-3">
                <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  <MapPin className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Location
                </h3>
                <div className="space-y-1 text-sm sm:text-base text-foreground/90">
                  <p className="font-bold text-primary">
                    Nova Southeastern University
                  </p>
                  <p className="text-foreground/80">
                    3100 Ray Ferrero Jr. Blvd.
                  </p>
                  <p className="text-foreground/80">Davie, FL 33314</p>
                </div>
              </div>

              {/* Card 3: Free Participation */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-3">
                <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  <Heart className="h-6 w-6" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Registration &amp; Giving
                </h3>
                <div className="space-y-1 text-sm sm:text-base text-foreground/90">
                  <p className="font-bold text-primary">
                    Participation is free.
                  </p>
                  <p className="text-foreground/80">
                    Fundraising is encouraged.
                  </p>
                  <p className="text-xs text-muted-foreground pt-1">
                    Every contribution helps NAMI Broward County expand mental
                    health services.
                  </p>
                </div>
              </div>
            </div>

            {/* Section CTA */}
            <div className="mt-8 text-center">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-md h-12 px-6 sm:px-8"
              >
                <a
                  href={TEAM_REGISTRATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <span>REGISTER WITH OUR TEAM</span>
                  <ExternalLink className="h-4 w-4 shrink-0" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* WHY WE'RE WALKING */}
        <section className="py-12 md:py-16 bg-background">
          <div className="container-px mx-auto max-w-6xl">
            <div className="max-w-3xl mb-10 space-y-3">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-accent">
                Our Community
              </span>
              <h2 className="font-headings text-2xl sm:text-3xl md:text-4xl font-extrabold text-primary">
                Walking Together for Mental Health
              </h2>
              <p className="text-foreground/90 text-base leading-relaxed">
                At Neuro Wellness TMS Centers of America, supporting mental
                health extends beyond the walls of our practice. NAMIWalks gives
                our patients, families, team members, and community an
                opportunity to come together, help raise awareness, reduce
                stigma, and stand alongside those affected by mental health
                conditions.
              </p>
              <p className="text-foreground/80 text-base font-medium">
                We invite you to join us and walk as part of the Neuro Wellness
                community.
              </p>
            </div>

            {/* Four Simple Icon Cards */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* Hope */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-2 hover:border-accent/40 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Hope
                </h3>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  Standing with people who may be navigating difficult seasons.
                </p>
              </div>

              {/* Awareness */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-2 hover:border-accent/40 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Awareness
                </h3>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  Encouraging more open conversations about mental health.
                </p>
              </div>

              {/* Community */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-2 hover:border-accent/40 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Users className="h-5 w-5" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Community
                </h3>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  Bringing patients, families, professionals, and supporters
                  together.
                </p>
              </div>

              {/* Support */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-2 hover:border-accent/40 transition-colors">
                <div className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-3">
                  <Heart className="h-5 w-5" />
                </div>
                <h3 className="font-headings text-lg font-bold text-primary">
                  Support
                </h3>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  Supporting the mission of NAMI Broward County.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CAMPAIGN FEATURE SECTION (Distinctive Deep Navy with Accents) */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground py-14 md:py-20">
          {/* Subtle accent glow orbs */}
          <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
          <div className="pointer-events-none absolute right-0 bottom-0 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />

          <div className="container-px mx-auto max-w-5xl relative z-10">
            <div className="grid items-center gap-8 md:grid-cols-12">
              {/* Copy Side */}
              <div className="space-y-4 md:col-span-8 text-center md:text-left">
                <div className="inline-block rounded-full bg-accent/20 border border-accent/40 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-accent">
                  WALKING TOWARD
                </div>

                <h2 className="font-headings text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight text-white leading-tight">
                  HOPE • HEALING • REMISSION
                </h2>

                <p className="text-base sm:text-xl font-semibold text-accent pt-1">
                  A Healthier Mind. A Brighter Tomorrow.
                </p>

                <p className="text-sm sm:text-base text-primary-foreground/85 max-w-xl">
                  Whether you are currently receiving treatment, supporting a
                  loved one, or advocating for mental wellness across Broward
                  County, every step makes a difference.
                </p>

                <div className="pt-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-lg h-12 px-8"
                  >
                    <a
                      href={TEAM_REGISTRATION_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2"
                    >
                      <span>JOIN OUR TEAM</span>
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Mascot Side: Supporting visual accent from campaign artwork */}
              <div className="md:col-span-4 flex justify-center">
                <div className="relative w-36 sm:w-48 md:w-64 aspect-square rounded-2xl overflow-hidden bg-white/10 p-2 border border-white/20 shadow-2xl backdrop-blur-sm">
                  <img
                    src={CAMPAIGN_FLYER_IMAGE}
                    alt="Neuro Wellness NAMIWalks Brain Mascot"
                    className="w-full h-full object-cover object-center rounded-xl"
                    width={400}
                    height={400}
                    loading="lazy"
                  />
                  <div className="absolute inset-x-2 bottom-2 rounded-b-xl bg-primary/80 backdrop-blur-md px-2 py-1 text-center">
                    <span className="text-[11px] font-bold tracking-wider text-accent uppercase">
                      Stronger Minds • Brighter Tomorrows
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHO CAN JOIN */}
        <section className="py-12 md:py-16 bg-background">
          <div className="container-px mx-auto max-w-6xl">
            <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-accent">
                Everyone Is Welcome
              </span>
              <h2 className="font-headings text-2xl sm:text-3xl md:text-4xl font-extrabold text-primary">
                Walk With the Neuro Wellness Community
              </h2>
              <p className="text-foreground/90 text-sm sm:text-base leading-relaxed">
                You don't need to be a current patient to participate. Patients,
                family members, friends, caregivers, Neuro Wellness team
                members, and community supporters are all welcome.
              </p>
            </div>

            {/* Six Clean Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: "Patients",
                  desc: "Current and former patients celebrating progress, hope, and new beginnings.",
                },
                {
                  title: "Family Members",
                  desc: "Spouses, parents, adult children, and family standing shoulder to shoulder.",
                },
                {
                  title: "Friends",
                  desc: "Loved ones showing support and breaking the silence around mental health.",
                },
                {
                  title: "Caregivers",
                  desc: "Dedicated caregivers whose encouragement makes healing possible.",
                },
                {
                  title: "Neuro Wellness Team Members",
                  desc: "Our clinicians, technicians, and staff walking proudly alongside our community.",
                },
                {
                  title: "Community Supporters",
                  desc: "Advocates, neighbors, and partners passionate about mental wellness in South Florida.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-soft space-y-2 hover:border-accent/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-accent" />
                    <h3 className="font-headings text-lg font-bold text-primary">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-md h-12 px-4 sm:px-8 text-sm sm:text-base"
              >
                <a
                  href={TEAM_REGISTRATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-center"
                >
                  <span className="leading-tight">
                    YES — I WANT TO WALK WITH NEURO WELLNESS
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="py-14 md:py-20 bg-muted/60 border-t border-border">
          <div className="container-px mx-auto max-w-4xl text-center space-y-6">
            <span className="inline-block text-xs sm:text-sm font-bold uppercase tracking-wider text-accent">
              Stronger Minds. Brighter Tomorrows.
            </span>

            <h2 className="font-headings text-3xl sm:text-4xl md:text-5xl font-extrabold text-primary">
              Let's Walk Together
            </h2>

            <p className="text-base sm:text-lg text-foreground/90 max-w-2xl mx-auto leading-relaxed">
              Join Neuro Wellness at NAMIWalks Broward 2026 and help us show
              support for mental health awareness throughout South Florida.
            </p>

            <div className="pt-2">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-lg text-sm sm:text-base h-12 px-4 sm:px-8"
              >
                <a
                  href={TEAM_REGISTRATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-center"
                >
                  <span className="leading-tight">
                    JOIN THE NEURO WELLNESS TEAM
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0" />
                </a>
              </Button>
            </div>

            <p className="text-xs text-muted-foreground pt-1">
              You will be taken to the official NAMIWalks website to complete
              your registration.
            </p>
          </div>
        </section>
      </main>

      {/* MOBILE STICKY CTA BAR (Mobile-first printed QR code / smartphone visitors) */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border p-3 shadow-premium"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
      >
        <Button
          asChild
          size="lg"
          className="w-full bg-accent hover:bg-accent/90 text-white font-bold tracking-wide rounded-xl shadow-md h-12 text-sm sm:text-base justify-center"
        >
          <a
            href={TEAM_REGISTRATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2"
          >
            <span>JOIN OUR TEAM</span>
            <ExternalLink className="h-4 w-4 shrink-0" />
          </a>
        </Button>
      </div>

      <Footer />
    </div>
  );
};

export default NamiWalksPage;
